"use client";

import { ArrowLeft } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import type React from "react";
import { Link } from "@/i18n/navigation";

type Vendor = {
  id: string;
  shop_name: string;
  [key: string]: unknown;
};

interface VendorSearchResultsProps {
  vendors: Vendor[] | Vendor[][] | undefined;
  onResultClick: () => void;
}

// Flatten nested arrays
const flattenArray = <T,>(arr: T[] | T[][] | undefined): T[] => {
  if (!arr || arr.length === 0) return [];
  if (Array.isArray(arr[0])) {
    return (arr as T[][]).flat(2) as T[];
  }
  return arr as T[];
};

export const VendorSearchResults: React.FC<VendorSearchResultsProps> = ({
  vendors,
  onResultClick,
}) => {
  const t = useTranslations("nav.search");
  const vendorsList = flattenArray<Vendor>(
    vendors as unknown as Vendor[] | Vendor[][],
  );

  const hasVendors = vendorsList.length > 0;

  if (!hasVendors) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-sm text-text-secondary">{t("noResults")}</p>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-0">
      {vendorsList.map((vendor) => (
        <Link
          key={vendor.id}
          href={`/shops/${vendor.id}`}
          className="flex items-center justify-between py-3 border-b border-stroke-subtle last:border-b-0 hover:bg-bg-secondary/50 transition-colors"
          onClick={onResultClick}
        >
          <span className="text-sm font-medium truncate max-w-30">
            {vendor.shop_name}
          </span>
          <ArrowLeft
            size={24}
            className="text-text-secondary shrink-0"
            style={{ transform: "rotate(135deg)" }}
          />
        </Link>
      ))}
    </div>
  );
};
